// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
'use client';
// Déconnexion automatique d'une session inactive — socle partagé de la suite.
//
// CE QU'ELLE PROTÈGE : les postes du laboratoire sont partagés. Une paillasse
// quittée pour une urgence laisse une session ouverte sous le nom d'une
// personne ; tout ce qui serait signé, validé ou diffusé depuis cet écran
// porterait SON nom au journal chaîné. Le compte n'est pas le problème — la
// session oubliée l'est.
//
// CE QU'ELLE COÛTE : une saisie en cours jetée. C'est le seul défaut qui
// compte, parce qu'il est le seul qui fasse contourner la mesure — un
// technicien à qui l'on efface un formulaire à moitié rempli laisse un poste
// connecté en permanence dans la semaine. Trois règles en découlent :
//
//  1. On PRÉVIENT avant (`avertissementSec`), on ne coupe jamais sans préavis.
//  2. On laisse PROLONGER d'un clic, sans ressaisir quoi que ce soit.
//  3. On ne coupe JAMAIS quand l'hôte signale une saisie en cours
//     (`retenirSi`) : l'avertissement reste affiché, la coupure est reportée
//     indéfiniment. Perdre du travail n'est pas une option acceptable, même
//     au prix d'une session qui reste ouverte — c'est un arbitrage assumé.
//
// L'avertissement est une BANDE, pas une fenêtre modale : il ne prend pas le
// focus et ne recouvre pas le champ en cours de saisie.
//
// `delaiMinutes <= 0` désactive complètement le mécanisme (aucun écouteur,
// aucun minuteur) : c'est la valeur qui rend le réglage neutre.
//
// LE COOKIE SUIT LE MÊME DÉLAI (14/10/2026). Ce minuteur meurt avec l'onglet :
// fermer Chrome ne coupait rien, et la session restait ouverte 7 jours. Le
// cookie de session expire donc seul après `delaiMinutes` d'inactivité, et ce
// composant le renouvelle (`/api/auth/prolonger`) à l'ouverture puis à chaque
// geste, au plus une fois par quart de délai. Onglet fermé = plus de geste = le
// cookie s'éteint tout seul. Délai désactivé : un appel unique rend au cookie
// sa longue durée.
import React, { useCallback, useEffect, useRef, useState } from 'react';

/** Gestes comptés comme « la personne est là ». Volontairement SANS
 *  `mousemove` : un bras posé sur la paillasse ou une souris qui dérive
 *  garderaient la session ouverte indéfiniment. */
const EVENEMENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

export interface DeconnexionAutoProps {
  /** Délai d'inactivité avant coupure, en minutes. `0` = désactivée. */
  delaiMinutes: number;
  /** Durée de l'avertissement qui précède la coupure (défaut : 60 s). */
  avertissementSec?: number;
  /** Coupure effective : à l'hôte de faire partir /auth/logout et de remettre
   *  sa session à l'état public. */
  onDeconnexion: () => void | Promise<void>;
  /** Renvoie une RAISON de ne pas couper (« Modifications non enregistrées »),
   *  ou `null` si l'on peut couper. Appelée au moment de couper, jamais avant. */
  retenirSi?: () => string | null;
}

/** Réécrit le cookie de session pour `minutes` d'inactivité. Muet en cas
 *  d'échec : un renouvellement manqué sera repris au geste suivant, et ne doit
 *  jamais gêner la saisie. */
function renouvelerCookie(minutes: number): void {
  void fetch('/api/auth/prolonger', {
    method: 'POST', credentials: 'include', keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ minutes: Math.max(0, minutes) }),
  }).catch(() => {});
}

function mmss(s: number): string {
  const m = Math.floor(s / 60);
  return m > 0 ? `${m} min ${String(s % 60).padStart(2, '0')} s` : `${s} s`;
}

export function DeconnexionAuto({
  delaiMinutes, avertissementSec = 60, onDeconnexion, retenirSi,
}: DeconnexionAutoProps) {
  // `restant` = secondes avant coupure pendant l'avertissement ; `null` = pas
  // d'avertissement affiché. `retenu` = raison pour laquelle on ne coupe pas.
  const [restant, setRestant] = useState<number | null>(null);
  const [retenu, setRetenu] = useState<string | null>(null);

  // Échéance et état « averti » vivent dans des refs : ils changent à la
  // seconde et ne doivent PAS provoquer de rendu ni relancer le minuteur.
  const echeance = useRef(0);
  const averti = useRef(false);
  // Les rappels de l'hôte sont relus à chaque tour via une ref : le minuteur
  // ne se recrée donc pas quand l'hôte redéfinit ses fonctions à chaque rendu.
  const rappels = useRef<{ onDeconnexion: DeconnexionAutoProps['onDeconnexion']; retenirSi?: () => string | null }>({ onDeconnexion });
  useEffect(() => { rappels.current = { onDeconnexion, retenirSi }; });

  const prolonger = useCallback(() => {
    echeance.current = Date.now() + Math.max(1, delaiMinutes) * 60000;
    averti.current = false;
    renouvelerCookie(delaiMinutes);
    setRestant(null);
    setRetenu(null);
  }, [delaiMinutes]);

  const couperMaintenant = useCallback(() => {
    setRestant(null);
    setRetenu(null);
    void rappels.current.onDeconnexion();
  }, []);

  useEffect(() => {
    // Désactivée : plus de minuteur, mais le cookie doit retrouver sa longue durée.
    if (!(delaiMinutes > 0)) { renouvelerCookie(0); return; }
    const delaiMs = delaiMinutes * 60000;
    echeance.current = Date.now() + delaiMs;
    averti.current = false;

    // Le cookie est renouvelé à l'ouverture (il a été posé à la connexion avec
    // un délai par défaut) puis, au plus, une fois par quart de délai.
    const pasRenouvellement = Math.min(delaiMs / 4, 5 * 60000);
    let dernierRenouvellement = Date.now();
    renouvelerCookie(delaiMinutes);

    // Une activité repousse l'échéance TANT QUE l'avertissement n'est pas
    // affiché. Une fois averti, seul un clic explicite prolonge : sinon le
    // moindre mouvement annulerait l'avertissement et la mesure ne servirait
    // plus à rien.
    const onActivite = () => {
      if (averti.current) return;
      echeance.current = Date.now() + delaiMs;
      if (Date.now() - dernierRenouvellement >= pasRenouvellement) {
        dernierRenouvellement = Date.now();
        renouvelerCookie(delaiMinutes);
      }
    };
    for (const e of EVENEMENTS) window.addEventListener(e, onActivite, { passive: true });

    const iv = window.setInterval(() => {
      const reste = Math.ceil((echeance.current - Date.now()) / 1000);

      if (reste > avertissementSec) {
        if (averti.current) { averti.current = false; setRestant(null); setRetenu(null); }
        return;
      }
      averti.current = true;
      if (reste > 0) { setRestant(reste); return; }

      // Échéance atteinte. Dernière chance donnée à l'hôte de retenir la
      // coupure — on ne jette JAMAIS une saisie en cours.
      const raison = rappels.current.retenirSi?.() || null;
      if (raison) {
        setRetenu(raison);
        // On réarme EXACTEMENT la fenêtre d'avertissement — ni plus, ni moins.
        // Plus longue, on repasserait par la branche « reste > avertissement »
        // qui efface la bande : le message de report ne durerait qu'un tour
        // d'horloge et disparaîtrait sous les yeux de la personne (défaut
        // constaté au montage). Ici la bande reste affichée en continu, et la
        // condition de report est réévaluée à chaque fin de fenêtre.
        echeance.current = Date.now() + Math.max(avertissementSec, 10) * 1000;
        return;
      }
      window.clearInterval(iv);
      setRestant(null);
      void rappels.current.onDeconnexion();
    }, 1000);

    return () => {
      window.clearInterval(iv);
      for (const e of EVENEMENTS) window.removeEventListener(e, onActivite);
    };
  }, [delaiMinutes, avertissementSec]);

  if (!(delaiMinutes > 0) || restant === null) return null;

  return (
    <div role="alert" aria-live="assertive" style={{
      position: 'fixed', insetInlineStart: 0, insetInlineEnd: 0, bottom: 0, zIndex: 400,
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, flexWrap: 'wrap',
      padding: '12px 18px', background: 'var(--navy-900, #0B1426)', color: '#fff',
      fontFamily: 'var(--font-sans)', fontSize: 13.5, lineHeight: 1.5,
      boxShadow: '0 -6px 18px rgba(11,20,38,0.28)',
    }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, flex: '1 1 340px' }}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="var(--amber-600, #C77700)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}>
          <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
        </svg>
        {retenu ? (
          <span>
            <strong>Session inactive — déconnexion suspendue.</strong>{' '}
            {retenu} : rien ne sera coupé tant que ce n’est pas rangé. Enregistrez, puis fermez votre session.
          </span>
        ) : (
          <span>
            <strong>Session inactive.</strong> Déconnexion automatique dans{' '}
            <strong style={{ fontFamily: 'var(--font-mono)' }}>{mmss(restant)}</strong>. Rien n’est enregistré à votre place.
          </span>
        )}
      </span>
      <span style={{ display: 'inline-flex', gap: 9, flex: 'none' }}>
        <button onClick={prolonger} style={{
          background: '#fff', color: 'var(--navy-900, #0B1426)', border: 'none',
          borderRadius: 'var(--radius-md, 8px)', padding: '8px 15px',
          fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 700, cursor: 'pointer',
        }}>Rester connecté</button>
        <button onClick={couperMaintenant} style={{
          background: 'transparent', color: 'rgba(255,255,255,0.82)',
          border: '1px solid rgba(255,255,255,0.28)', borderRadius: 'var(--radius-md, 8px)',
          padding: '8px 15px', fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>Se déconnecter</button>
      </span>
    </div>
  );
}
